import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { verify2FA, send2FAEmail } from '@/utils/api';
import { Shield, Mail, Smartphone, Loader2, ArrowLeft } from 'lucide-react';

const TwoFactorVerify = ({ cedula, tempToken, emailHint, onSuccess, onCancel }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [method, setMethod] = useState('totp');
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, [method]);

  const handleCodeChange = (index, value) => {
    // Limpiar no-dígitos
    const cleanValue = value.replace(/\D/g, '');
    
    // Si se pegan múltiples dígitos (ej: código completo)
    if (cleanValue.length > 1) {
      const newCode = [...code];
      for (let i = 0; i < cleanValue.length && i < 6; i++) {
        newCode[i] = cleanValue[i];
      }
      setCode(newCode);
      // Focus en el último campo llenado o el siguiente vacío
      const focusIndex = Math.min(cleanValue.length, 5);
      inputRefs.current[focusIndex]?.focus();
      return;
    }
    
    // Solo un dígito
    const digit = cleanValue.slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    // Focus last filled input or next empty
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Ingresa el código completo de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      const result = await verify2FA(cedula, fullCode, method);
      toast.success('Verificación exitosa');
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código incorrecto');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await send2FAEmail(cedula);
      toast.success(result.message);
      setEmailSent(true);
      setMethod('email');
      setCode(['', '', '', '', '', '']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-2">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Verificación en 2 Pasos</CardTitle>
          <CardDescription>
            {method === 'totp' 
              ? 'Ingresa el código de tu app autenticadora'
              : `Ingresa el código enviado a ${emailHint}`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Method indicator */}
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
            {method === 'totp' ? (
              <>
                <Smartphone className="w-4 h-4" />
                <span>Google Authenticator</span>
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                <span>Código por Email</span>
              </>
            )}
          </div>

          {/* Code input */}
          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-2xl font-bold"
                data-testid={`2fa-code-${index}`}
              />
            ))}
          </div>

          {/* Verify button */}
          <Button 
            onClick={handleVerify} 
            disabled={loading || code.join('').length !== 6}
            className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="verify-2fa-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              'Verificar'
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">
                ¿No tienes acceso?
              </span>
            </div>
          </div>

          {/* Backup method */}
          {method === 'totp' && (
            <Button
              variant="outline"
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="w-full"
              data-testid="send-email-2fa-button"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar código por email
                </>
              )}
            </Button>
          )}

          {method === 'email' && !emailSent && (
            <Button
              variant="outline"
              onClick={() => setMethod('totp')}
              className="w-full"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Usar app autenticadora
            </Button>
          )}

          {method === 'email' && emailSent && (
            <Button
              variant="ghost"
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="w-full text-sm"
            >
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Reenviar código
            </Button>
          )}

          {/* Cancel button */}
          <Button
            variant="ghost"
            onClick={onCancel}
            className="w-full text-slate-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio de sesión
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorVerify;
